import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children, requireAuth = true, requireApproval = false, requireRole = null, allowPasswordChange = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If route requires authentication and user is not logged in
  if (requireAuth && !user) {
    return <Navigate to="/login" replace />;
  }

  // Force temporary-password change before any other member page
  if (user && user.must_change_password && !allowPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // If route requires approval and user is not approved
  if (requireApproval && user && !user.is_approved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Membership Pending Approval</h2>
          <p className="text-gray-600 mb-6">
            Your membership application is being reviewed by our administrators. 
            You will receive an email once your membership is approved.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-[#2c3e7d] text-white px-6 py-2 rounded-full hover:bg-[#1e2a5a] transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // If route requires specific role
  if (requireRole && user && user.role !== requireRole && !['super_admin', 'admin', 'executive'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-[#2c3e7d] text-white px-6 py-2 rounded-full hover:bg-[#1e2a5a] transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
